import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchDbuTreeList, fetchRunTimeTreeList, fetchTopSpendingTreeList, workspaceGetEnv, fetchAggregateClusterDataByDate, getCountByCategoryTreeData, getCostByCategoryTreeData, getNewCountByCategoryTreeData, getQueryTrendTreeData, getDbuByWarehouseTreeData, getDbuGroupTreeData, getCostGroupTreeData, getCatalogCountTreeData, getCatalogCostTreeData, getFailedQueryCostTreeData, fetchSectorList } from 'service/service';
import moment from 'moment';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import LoaderComponent from 'components/Loader';
import FiltersBar from './FilterBar';
import DetailedViewWidget from 'components/Widgets/DetailedViewWidget';
import RowTypography from 'components/Table/RowTypography';
import PageWrapper from 'components/Common/PageWraper';
import { extractDatesFromNode, findSelectedNode, currencyFormatter, formatCompactNumber, formatHoursAsDays } from 'utils/common-util';

const defaultColumnNames = (searchSource: string = '') => {
    const isCountBased = [
        'catalogsCount', 'userByDbuUsage', 'dbuUsageSqlWarehouses',
        'warehouseByCategory', 'totalCountByCategory', 'totalCountClusterByCategory',
        'clustersCountWithoutAutoTermination', 'clusterSpinUpsByCategory'
    ].includes(searchSource);

    if (isCountBased) {
        return ['Time Range', 'Count', '% of Total'];
    }
    return ['Time Range', 'Cost', 'Spend', 'Compute Spend', 'RunTime Hours', 'DBU', '% of Total'];
};

const COST_KEYS = [
    'catalogCost', 'dollarsEstimated', 'dbuEstimated', 'sqlWarehouseCost',
    'sqlWarehouseDbu', 'distinctStatementIDCount', 'distinctWarehouseIDCount',
    'cost', 'costcompute', 'totalComputeCost', 'runTimeHours',
    'totalRunTimeHours', 'dbu', 'totalDBU', 'total_cluster_cost',
    'count', 'totalCount'
];

import { unwrap, resolveCost } from 'utils/adb-common-util';

const SERVICE_MAPPING: Record<string, any> = {
    topSpendingWorkspace: fetchTopSpendingTreeList,
    computeSpend: fetchTopSpendingTreeList,
    workspaceRuntimeCost: fetchTopSpendingTreeList,
    workspaceRuntimeRunTime: fetchRunTimeTreeList,
    runtime: fetchRunTimeTreeList,
    dbuWorkspaceRuntimeRunTime: fetchRunTimeTreeList,
    dbuWorkspaceRuntimeDBU: fetchDbuTreeList,
    totalCountClusterByCategory: fetchAggregateClusterDataByDate,
    costSpendByCategory: fetchAggregateClusterDataByDate,
    totalCostByClusterWithAutoTermination: fetchAggregateClusterDataByDate,
    clustersCountWithoutAutoTermination: fetchAggregateClusterDataByDate,
    clusterProvisionedByCategory: fetchAggregateClusterDataByDate,
    clusterSpinUpsByCategory: fetchAggregateClusterDataByDate,
    topTenClusterCostbyComputeJob: fetchAggregateClusterDataByDate,
    topTenClusterCostbyComputeInteractive: fetchAggregateClusterDataByDate,
};

const FILTERED_SERVICE_MAPPING: Record<string, { api: any; field: string; stateKey: string }> = {
    totalCountByCategory: { api: getCountByCategoryTreeData, field: 'warehouse_type', stateKey: 'warehouseType' },
    totalCostByCategory: { api: getCostByCategoryTreeData, field: 'warehouse_type', stateKey: 'warehouseType' },
    warehouseCostTrend: { api: getCostByCategoryTreeData, field: 'warehouse_type', stateKey: 'warehouseType' },
    costSqlWarehouse: { api: getCostByCategoryTreeData, field: 'warehouse_type', stateKey: 'warehouseType' },
    warehouseByCategory: { api: getNewCountByCategoryTreeData, field: 'warehouse_type', stateKey: 'warehouseType' },
    queryVolumeTrend: { api: getQueryTrendTreeData, field: 'warehouse_type', stateKey: 'warehouseType' },
    dbuUsageSqlWarehouses: { api: getDbuByWarehouseTreeData, field: 'warehouse_name', stateKey: 'warehouseName' },
    userByDbuUsage: { api: getDbuGroupTreeData, field: 'executed_by', stateKey: 'userExecutedBy' },
    userByCost: { api: getCostGroupTreeData, field: 'executed_by', stateKey: 'userExecutedBy' },
    catalogsCount: { api: getCatalogCountTreeData, field: 'catalog_name', stateKey: 'catalogName' },
    catalogsCost: { api: getCatalogCostTreeData, field: 'catalog_name', stateKey: 'catalogName' },
};

const ROUTE_PATH_MAPPING: Record<string, string> = {
    computeSpend: 'compute-spend-details',
    runtime: 'runTime_table',
    totalCountClusterByCategory: 'clusterCount_table',
    costSpendByCategory: 'cost_spend_table',
    totalCostByClusterWithAutoTermination: 'costClusterWithoutAutoTermination',
    clustersCountWithoutAutoTermination: 'clusterCountWithoutAutoTermination',
    clusterProvisionedByCategory: 'clusterProvisionedByCategory_details',
    clusterSpinUpsByCategory: 'clusterSpinsupByCategory_details',
    topTenClusterCostbyComputeJob: 'topTenClusterCost_job',
    topTenClusterCostbyComputeInteractive: 'topTenClusterCost_interactive',
};

const SQL_DETAILS_SOURCES = [
    'totalCountByCategory', 'totalCostByCategory', 'warehouseByCategory',
    'warehouseCostTrend', 'dbuUsageSqlWarehouses', 'costSqlWarehouse',
    'queryVolumeTrend', 'userByDbuUsage', 'userByCost',
    'catalogsCount', 'catalogsCost', 'failedqueriesdetails'
];

const resolveRoutePath = (source: string) => {
    if (ROUTE_PATH_MAPPING[source]) return ROUTE_PATH_MAPPING[source];
    if (source.includes('workspaceRuntime')) return 'costVsworkspaceRunTimeTable';
    if (source.includes('dbuWorkspaceRuntime')) return 'dbuVsworkspaceRunTimeTable';
    if (SQL_DETAILS_SOURCES.includes(source)) return 'SQLDetails';
    return 'top-spending-workspace';
};

const getRowType = (id: string): 'year' | 'month' | 'week' | 'day' => {
    if (id.includes('day')) return 'day';
    if (id.includes('week')) return 'week';
    if (id.includes('month')) return 'month';
    return 'year';
};

const deriveNavDates = (found: any) => {
    if (!found?.kind || !found?.node) return null;
    const { kind, node } = found;
    if (kind === 'day' && node.date) {
        const d = moment(node.date, 'YYYY-MM-DD').format('YYYY-MM-DD');
        return { startDate: d, endDate: d, selectedDateInString: 'customDateRange' };
    }
    const dates = extractDatesFromNode(node);
    if (dates.length > 0) {
        const moments = dates.map((ds: string) => moment(ds, 'YYYY-MM-DD'));
        return {
            startDate: moment.min(moments).format('YYYY-MM-DD'),
            endDate: moment.max(moments).format('YYYY-MM-DD'),
            selectedDateInString: 'customDateRange'
        };
    }
    return null;
};

const parseFilters = (rawFilters: any, state: any, field: string, stateKey: string) => {
    let parsed: any[] = [];
    if (rawFilters) {
        try {
            parsed = typeof rawFilters === 'string' ? JSON.parse(rawFilters) : rawFilters;
        } catch (e) {
            console.error('Filter parse error:', e);
        }
    }
    if (parsed.length === 0 && state?.[stateKey]) {
        return [{ operator: 'AND', field, value: state[stateKey] }];
    }
    return parsed;
};

function getColumns(detailedRef: any, handleRowClick: any, costLabel: string = 'Cost', searchSource: string = '', detailedTableTitle: string = '') {

    const isCountBased = [
        'catalogsCount', 'userByDbuUsage', 'dbuUsageSqlWarehouses',
        'warehouseByCategory', 'totalCountByCategory', 'totalCountClusterByCategory',
        'clustersCountWithoutAutoTermination', 'clusterSpinUpsByCategory'
    ].includes(searchSource);

    const dataKey = isCountBased ? 'count' : 'cost';
    const displayLabel = isCountBased ? 'Count' : costLabel;

    return [
        {
            key: 'time_range',
            name: 'Time Range',
            formatter({ row, isCellSelected }: any) {
                const hasChildren = row.values && row.values.length > 0;
                const indentLevel = row.level || 0;
                const indentSpace = indentLevel * 16;
                return (
                    <Box display="flex">
                        <Box display="flex" alignItems="center" ml={`${indentSpace}px`} flexGrow={1} overflow="hidden">
                            {hasChildren && (
                                <CellExpanderFormatter
                                    isCellSelected={isCellSelected}
                                    expanded={row.isExpanded}
                                    onCellExpand={() => {
                                        return detailedRef.current.toggleSubRow(row.id);
                                    }}
                                />
                            )}
                            <RowTypography
                                sx={{
                                    flexGrow: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 'calc(100% - 40px)',
                                    marginLeft: "8px",
                                    marginRight: "4px"
                                }}
                            >
                                {row.time_range}
                            </RowTypography>
                        </Box>
                    </Box>
                );
            }
        },
        {
            key: dataKey,
            name: displayLabel,
            formatter({ row }: any) {
                const raw = row?.cost;
                const num = typeof raw === 'string' ? Number(raw) : raw;
                let display;

                if (['workspaceRuntimeRunTime', 'runtime', 'dbuWorkspaceRuntimeRunTime'].includes(searchSource)) {
                    display = (typeof num === 'number' && Number.isFinite(num))
                        ? formatHoursAsDays(num) : (raw ?? '--');
                } else if (searchSource === 'catalogsCount' || searchSource === 'userByDbuUsage' || searchSource === 'dbuUsageSqlWarehouses' || searchSource === 'warehouseByCategory' || searchSource === 'totalCountByCategory' || searchSource === 'dbuWorkspaceRuntimeDBU' || searchSource === 'totalCountClusterByCategory' || searchSource === "clustersCountWithoutAutoTermination" || searchSource === 'clusterSpinUpsByCategory') {
                    display = (typeof num === 'number' && Number.isFinite(num))
                        ? formatCompactNumber(num) : (raw ?? '--');
                } else {
                    display = (typeof num === 'number' && Number.isFinite(num)) ? currencyFormatter.format(num) : (raw ?? '--');
                }

                return (
                    <Box
                        sx={{ cursor: 'pointer', color: '#4fc3f7' }}
                        onClick={() => handleRowClick(row)}
                    >
                        {display}
                    </Box>
                );
            }
        },
        {
            key: 'percent_of_total',
            name: '% of Total'
        }
    ];
}

const ADBTimeRangeView = () => {
    const routerLocation = useLocation();
    const navigate = useNavigate();
    const navState = routerLocation.state;

    const {
        startDate: navStartDate,
        endDate: navEndDate,
        env: navEnv,
        sector: navSector,
        location: navLocation,
        selectedDateInString: navDateString,
        searchSource: navSource,
        detailedTableTitle: navTitle,
        detailedTableType: navTableType,
        clickedStartDate,
        clickedEndDate,
        pageHeader: navPageHeader,
        pageSubHeader: navPageSubHeader,
        contentName: navContentName,
        searchPoint,
        filters: navFilters,
        clickedItemName
    } = navState || {};

    const [startDate, setStartDate] = useState(navStartDate || moment().subtract(3, 'months').startOf('day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(navEndDate || moment().format('YYYY-MM-DD'));
    const [env, setEnv] = useState(navEnv || 'both');
    const [sector, setSector] = useState(navSector || 'all');
    const [sectorName, setSectorName] = useState(navSector || 'all');
    const [location, setLocation] = useState(navLocation || 'South Central US');
    const [selectedDateInString, setSelectedDateInString] = useState(navDateString || '3M');
    const [searchSource, setSearchSource] = useState(navSource || '');
    const [detailedTableTitle, setDetailedTableTitle] = useState(navTitle || '');
    const [detailedTableType, setDetailedTableType] = useState(navTableType || '');
    const [pageHeader, setPageHeader] = useState(navPageHeader || '');
    const [pageSubHeader, setPageSubHeader] = useState(navPageSubHeader || '');
    const [contentName, setContentName] = useState(navContentName || '');
    const [filters, setFilters] = useState(navFilters || []);

    const [treeListData, setTreeListData] = useState<any>(null);
    const [apiResponseData, setApiResponseData] = useState<any[]>([]);
    const [totalSum, setTotalSum] = useState(0);
    const [loader, setLoader] = useState(false);
    const [sectorList, setSectorList] = useState<string[]>([]);
    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);

    const initialEnvAppliedRef = useRef(false);
    const restoredRef = useRef(false);
    const envRef = useRef(env);
    useEffect(() => { envRef.current = env; }, [env]);

    const detailedRef = useRef<any>(null);
    const PRESERVE_FILTER_KEY = 'adb_timerange_filters';

    const calculateDataTotal = (input: any) => {
        if (!input) return 0;
        const root = (input && input.data !== undefined) ? input.data : input;
        let data: any[] = [];
        if (Array.isArray(root)) {
            data = root;
        } else if (root && typeof root === 'object') {
            data = [root];
        }

        if (data.length === 0) return 0;
        return data.reduce((sum: number, wrapper: any) => {
            if (!wrapper || typeof wrapper !== 'object') return sum;
            let innerSum = 0;
            const keys = Object.keys(wrapper);
            for (const k of keys) {
                const node = wrapper[k];
                if (node && typeof node === 'object' && (node.year_name || node.month_name || node.week_name || node.day_name)) {
                    innerSum += resolveCost(node);
                }
            }
            return sum + (innerSum || resolveCost(unwrap(wrapper)));
        }, 0);
    };

    function flattenCostData(input: any): any[] {
        const rows: any[] = [];
        if (!input) return [];

        const root = (input && input.data !== undefined) ? input.data : input;
        let data: any[] = [];

        if (Array.isArray(root)) {
            data = root;
        } else if (root && typeof root === 'object') {
            const matches = Object.keys(root)
                .filter(k => (typeof root[k] === 'object' && root[k] !== null) && (root[k].year_name || root[k].month_name || root[k].week_name || root[k].day_name))
                .map(k => ({ [k]: root[k] }));

            if (matches.length > 0) {
                data = matches;
            } else {
                data = [root];
            }
        }

        const currentTotal = calculateDataTotal(data);
        const formatPct = (val: number) => {
            if (!currentTotal) return '0.00%';
            return `${((val / currentTotal) * 100).toFixed(2)}%`;
        };

        const annotateNode = (nodeWrapper: any) => {
            const node = unwrap(nodeWrapper);
            if (!node || typeof node !== 'object') return;
            const inner = node.year || node.month || node.week || node.day;
            const target = inner || node;
            if (target.year_name || target.month_name || target.week_name || target.day_name) {
                target.percent_of_total = formatPct(resolveCost(target));
            }
            (target.children || []).forEach(annotateNode);
        };
        data.forEach(annotateNode);

        const identifyNode = (node: any) => {
            if (!node || typeof node !== 'object') return null;
            if (node.year_name) return { type: 'year' as const, name: node.year_name, isLeaf: false };
            if (node.month_name) return { type: 'month' as const, name: node.month_name, isLeaf: false };
            if (node.week_name) return { type: 'week' as const, name: node.week_name, isLeaf: false };
            if (node.day_name) return { type: 'day' as const, name: node.day_name, isLeaf: true };
            if (node.date) return { type: 'day' as const, name: node.date, isLeaf: true };
            return null;
        };

        const findNodeInfo = (node: any) => {
            if (!node || typeof node !== 'object') return null;
            const target = node.year || node.month || node.week || node.day || node;
            const info = identifyNode(target);
            return info ? { item: target, ...info } : null;
        };

        function getInfo(obj: any) {
            const direct = findNodeInfo(obj);
            if (direct) return direct;
            const EXCLUDES = ['distinctWarehouseIDCount', 'distinctStatementIDCount', 'success', 'totalElements', 'totalPages'];
            for (const key of Object.keys(obj || {})) {
                if (EXCLUDES.includes(key)) continue;
                const sub = findNodeInfo(obj[key]);
                if (sub) return sub;
            }
            return null;
        }

        const isCountBased = [
            'catalogsCount', 'userByDbuUsage', 'dbuUsageSqlWarehouses',
            'warehouseByCategory', 'totalCountByCategory', 'totalCountClusterByCategory',
            'clustersCountWithoutAutoTermination', 'clusterSpinUpsByCategory'
        ].includes(searchSource);
        const dataKey = isCountBased ? 'count' : 'cost';

        function addRowsCore(obj: any, level: number, parentId: string) {
            const info = getInfo(obj);
            if (!info) return;
            const { item, type, name, isLeaf } = info;
            const costVal = resolveCost(item);
            const id = parentId ? `${parentId}-${type}-${name}` : `${type}-${name}`;
            rows.push({
                id,
                time_range: (type === 'day' && name === 'Yesterday') ? item.date : name,
                [dataKey]: costVal,
                cost: costVal,
                percent_of_total: item.percent_of_total ?? formatPct(costVal),
                values: isLeaf ? [] : (item.children || []),
                isExpanded: false,
                level,
                ...(isLeaf && { records: item.records || [] })
            });
        }

        data.forEach(obj => addRowsCore(obj, 0, ''));
        return rows;
    }

    let parentLocation = 'workspace';
    if (pageHeader === 'DBSQL Performance') {
        parentLocation = 'dbsqlperfomance';
    } else if (pageHeader === 'Cluster Analytics') {
        parentLocation = 'clusteranalytics';
    } else if (pageHeader === 'Cost Management & Financial Analysis') {
        parentLocation = 'costmanagement';
    }

    const breadcrumbPath = `/databricksADB/${parentLocation}`;

    const updateHistoryState = (overrides: Partial<{
        startDate: string;
        endDate: string;
        env: string;
        sector: string;
        location: string;
        selectedDateInString: string;
        filters?: any;
        contentName?: string;
        workspaceName?: string;
        warehouseName?: string;
        warehouseType?: string;
        userExecutedBy?: string;
        catalogName?: string;
    }>) => {
        const newState = {
            startDate,
            endDate,
            clickedStartDate,
            clickedEndDate,
            env,
            sector,
            location,
            selectedDateInString,
            searchSource,
            contentName,
            filters,
            pageHeader,
            pageSubHeader,
            detailedTableTitle,
            detailedTableType,
            warehouseType: navState?.warehouseType,
            warehouseName: navState?.warehouseName,
            workspaceName: navState?.workspaceName,
            searchPoint: navState?.searchPoint,
            clickedItemName: navState?.clickedItemName,
            ...overrides
        };
        navigate(routerLocation.pathname, { state: newState, replace: true });
    };

    const computeCostLabel = (() => {
        if (!searchSource) return 'Cost';
        if (searchSource === 'topSpendingWorkspace') return 'Spend';
        if (searchSource === 'computeSpend') return 'Compute Spend';
        if (searchSource === 'workspaceRuntimeRunTime' || searchSource === 'runtime'
            || searchSource === 'dbuWorkspaceRuntimeRunTime') return 'RunTime Hours';
        if (searchSource === 'dbuWorkspaceRuntimeDBU') return 'DBU';
        if (searchSource === 'totalCountByCategory') return 'Count';
        if (searchSource === 'dbuUsageSqlWarehouses') return 'Count';
        if (searchSource === 'userByDbuUsage') return 'Count';
        if (searchSource === 'catalogsCount') return 'Count';
        // fallback
        return 'Cost';
    })();

    const fetchSectors = async () => {
        const resp = await fetchSectorList({});
        if (resp?.success) {
            setSectorList(resp?.data || []);
        }
    };

    const handleDateSelect = (stDt: string, edDt: string, selectedDate?: string) => {
        const dt = selectedDate;
        const selected = dt || '3M';
        setSelectedDateInString(selected);
        const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
        const end = yesterday.clone().endOf('day');
        let start;

        if (selected === 'Hrs') {
            start = yesterday.clone().startOf('day');
        } else if (selected === '7d') {
            start = yesterday.clone().subtract(6, 'days').startOf('day');
        } else if (selected === '30d') {
            start = yesterday.clone().subtract(29, 'days').startOf('day');
        } else if (selected === '3M') {
            start = yesterday.clone().subtract(3, 'months').startOf('day');
        } else if (selected === 'custom') {
            start = moment(stDt).startOf('day');
            const customEnd = moment(edDt).endOf('day');
            setStartDate(start.format('YYYY-MM-DD'));
            setEndDate(customEnd.format('YYYY-MM-DD'));
            return;
        } else {
            start = moment(stDt).startOf('day');
        }
        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
    }

    const computeDateRangeFromFound = (found: any, type: any) => {
        if (!found?.node) return null;

        const collectDatesFromDay = (node: any) => {
            const d = node.date || node.jobRunDate;
            return d ? [d] : [];
        };

        const collectDatesFromWeek = (wk: any) => {
            const dates: string[] = [];
            for (const dayWrapper of (wk.children || [])) {
                const key = Object.keys(dayWrapper)[0];
                const day = dayWrapper[key];
                if (day?.date) dates.push(day.date);
                else if (day?.jobRunDate) dates.push(day.jobRunDate);
            }
            return dates;
        };

        const collectDatesFromMonth = (month: any) => {
            const dates: string[] = [];
            for (const weekWrapper of (month.children || [])) {
                const wkKey = Object.keys(weekWrapper)[0];
                const wk = weekWrapper[wkKey];
                for (const dayWrapper of (wk.children || [])) {
                    const dKey = Object.keys(dayWrapper)[0];
                    const d = dayWrapper[dKey];
                    if (d?.date) dates.push(d.date);
                    else if (d?.jobRunDate) dates.push(d.jobRunDate);
                }
            }
            return dates;
        };

        if (type === 'day') {
            const dates = collectDatesFromDay(found.node);
            if (!dates.length) return null;
            const formatted = moment(dates[0]).format('YYYY-MM-DD');
            return { startDate: formatted, endDate: formatted };
        }

        let dates: string[] = [];
        if (type === 'week') {
            dates = collectDatesFromWeek(found.node);
        } else if (type === 'month') {
            dates = collectDatesFromMonth(found.node);
        }

        if (!dates.length) return null;
        const sorted = dates.map(d => moment(d)).sort((a, b) => a.valueOf() - b.valueOf());
        const sortedLength = sorted.length - 1;
        return { startDate: sorted[0].format('YYYY-MM-DD'), endDate: sorted[sortedLength].format('YYYY-MM-DD') };
    }

    const handleRowClick = useCallback((row: any) => {
        const type = getRowType(row.id);
        const found = findSelectedNode(apiResponseData, type, row.time_range, row.id);
        const computedRange = computeDateRangeFromFound(apiResponseData, type);

        const navStateBase: any = {
            dateLabel: row.time_range,
            apiData: found ?? { kind: 'full', node: apiResponseData },
            type,
            startDate: computedRange?.startDate || startDate,
            endDate: computedRange?.endDate || endDate,
            env,
            sector,
            location,
            selectedDateInString,
            contentName,
            searchSource,
            detailedTableTitle,
            warehouseType: searchPoint?.name || navState?.warehouseType || '',
            pageHeader,
            pageSubHeader,
            detailedTableType,
            clickedItemName,
            filters,
            clickedStartDate,
            clickedEndDate,
            breadcrumbPath,
            workspaceName: navState.workspaceName,
            warehouseName: navState.warehouseName,
            categoryName: navState.categoryName,
            userExecutedBy: navState.userExecutedBy,
            catalogName: navState.catalogName,
            warehouseNameOptions: navState.warehouseNameOptions,
            userExecutedByOptions: navState.userExecutedByOptions,
            catalogNameFilterOptions: navState.catalogNameFilterOptions,
        };

        try {
            const dateUpdate = deriveNavDates(found);
            if (dateUpdate) Object.assign(navStateBase, dateUpdate);

            sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify({
                startDate, endDate, selectedDateInString
            }));
        } catch (err) {
            console.error('Failed to derive date range', err);
        }

        navigate(`/databricksADB/${resolveRoutePath(searchSource)}`, { state: navStateBase });
    }, [navigate, apiResponseData, startDate, endDate, env, sector, location, selectedDateInString, contentName, searchSource, detailedTableTitle, searchPoint, navState, pageHeader, pageSubHeader, detailedTableType, clickedItemName, filters, clickedStartDate, clickedEndDate]);


    const fetchTreeListData = async ({ pageNumber, size, sortingInfo }: any) => {
        const curPage = (size === 100) ? pageNumber : 1;
        const sDate = clickedStartDate || startDate;
        const eDate = clickedEndDate || endDate;

        try {
            setLoader(true);
            const req = { startDate: sDate, endDate: eDate, dateRange: selectedDateInString, env, sector, location, ...(filters && { filters }), searchSource, pageNumber: curPage, size, sortingInfo };
            let res;

            if (SERVICE_MAPPING[searchSource]) {
                res = await SERVICE_MAPPING[searchSource](req);
            } else if (FILTERED_SERVICE_MAPPING[searchSource]) {
                const cfg = FILTERED_SERVICE_MAPPING[searchSource];
                const pFilters = parseFilters(filters, navState, cfg.field, cfg.stateKey);
                res = typeof cfg.api === 'function' && cfg.api.length === 1
                    ? await cfg.api({
                        startDate: sDate,
                        endDate: eDate,
                        env,
                        sector,
                        location,
                        selectedDateInString,
                        page: curPage,
                        limit: size,
                        filters: pFilters
                    })
                    : await cfg.api(sDate, eDate, env, sector, location, selectedDateInString, curPage, size, undefined, undefined, pFilters);
            } else if (searchSource === 'failedqueriesdetails') {
                res = await getFailedQueryCostTreeData({
                    startDate: sDate,
                    endDate: eDate,
                    env,
                    sector,
                    location,
                    selectedDateInString,
                    page: curPage,
                    limit: size
                });
            }

            const finalRes = (res?.data !== undefined) ? res : { success: true, data: res };

            const effectiveData = finalRes.data;
            if (effectiveData) {
                const dataArray = Array.isArray(effectiveData) ? effectiveData : [effectiveData];
                setApiResponseData(dataArray);
                setTotalSum(calculateDataTotal(dataArray));
            }

            setTreeListData(finalRes);
            return finalRes;
        } catch (error) {
            console.error(error);
            return { data: [] };
        } finally {
            setLoader(false);
            if (restoredRef.current) {
                sessionStorage.removeItem(PRESERVE_FILTER_KEY);
                restoredRef.current = false;
            }
        }
    };
    const getDetailedTableTitleWithTotal = () => {
        const baseTitle = detailedTableTitle || pageSubHeader || 'Details';
        const isCountBased = [
            'catalogsCount',
            'userByDbuUsage',
            'dbuUsageSqlWarehouses',
            'warehouseByCategory',
            'totalCountByCategory',
            'totalCountClusterByCategory',
            'clustersCountWithoutAutoTermination',
            'clusterSpinUpsByCategory',
        ].includes(searchSource);

        if (totalSum > 0) {
            if (isCountBased || searchSource === 'dbuWorkspaceRuntimeDBU') {
                const formattedTotal = formatCompactNumber(totalSum);
                return `${baseTitle} (${formattedTotal})`;
            } else if (['workspaceRuntimeRunTime', 'runtime', 'dbuWorkspaceRuntimeRunTime'].includes(searchSource)) {
                const formattedTotal = formatHoursAsDays(totalSum);
                return `${baseTitle} (${formattedTotal})`;
            } else {
                const formattedTotal = currencyFormatter.format(totalSum);
                return `${baseTitle} (${formattedTotal})`;
            }
        }

        return baseTitle;
    };
    const handleGetTreeListTableData = async (pageNumber: any, size: any, sortingInfo: any) => {
        try {
            await fetchTreeListData({ pageNumber, size, sortingInfo });
        } catch (err) {
            console.error(err);
        }
    };

    const getEnvFetch = async (location: string) => {
        try {
            const res = await workspaceGetEnv(location);
            const options = [
                { label: 'ALL', value: 'both' },
                ...(res?.data || [])
            ];
            setEnvOptions(options);

            if (!initialEnvAppliedRef.current && navState?.env) {
                setEnv(navState.env);
                initialEnvAppliedRef.current = true;
            } else {
                const availableValues = options.map(o => o.value);
                const currentEnv = envRef.current;
                if (!availableValues.includes(currentEnv)) {
                    const fallback = availableValues.includes('both') ? 'both' : (availableValues[0] || 'both');
                    setEnv(fallback);
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getEnvFetch(location);
    }, [location]);

    useEffect(() => {
        const pageNumber = 1;
        const size = 100;
        const sortingInfo = { sortBy: '', sortDir: '' };
        localStorage.removeItem(detailedTableType);
        fetchTreeListData({ pageNumber, size, sortingInfo });
    }, [startDate, endDate, sector, env, location, selectedDateInString, searchSource]);

    // --- Helper Functions to reduce Cognitive Complexity ---
    const applyMetadata = useCallback((state: any) => {
        const setters: Record<string, (v: any) => void> = {
            searchSource: setSearchSource,
            detailedTableTitle: setDetailedTableTitle,
            detailedTableType: setDetailedTableType,
            pageHeader: setPageHeader,
            pageSubHeader: setPageSubHeader,
            contentName: setContentName
        };

        Object.entries(setters).forEach(([key, setter]) => {
            if (state[key] !== undefined && state[key] !== null) setter(state[key]);
        });

        if (state.filters && JSON.stringify(state.filters) !== JSON.stringify(filters)) {
            setFilters(state.filters);
        }
    }, [filters]);

    const applyBaseFilters = useCallback((state: any) => {
        if (state.env) setEnv(state.env);
        if (state.sector) {
            setSector(state.sector);
            setSectorName(state.sector);
        }
        if (state.location) setLocation(state.location);
    }, []);

    const applyDateFilters = useCallback((state: any, preservedRaw: string | null) => {
        const isCustom = ['custom', 'customDateRange'].includes(state.selectedDateInString?.trim() || '');

        if (preservedRaw && isCustom) {
            try {
                const p = JSON.parse(preservedRaw);
                if (p.startDate) setStartDate(p.startDate);
                if (p.endDate) setEndDate(p.endDate);
                if (p.selectedDateInString) setSelectedDateInString(p.selectedDateInString);
                restoredRef.current = true;
                return;
            } catch (err) {
                console.error('Failed to parse preserved filters', err);
            }
        }

        if (state.startDate) setStartDate(state.startDate);
        if (state.endDate) setEndDate(state.endDate);
        if (state.selectedDateInString) setSelectedDateInString(state.selectedDateInString);
    }, []);

    // Update filters when navigation state changes
    useEffect(() => {
        if (!navState) return;
        const preservedRaw = sessionStorage.getItem(PRESERVE_FILTER_KEY);

        applyMetadata(navState);
        applyBaseFilters(navState);
        applyDateFilters(navState, preservedRaw);

        initialEnvAppliedRef.current = false;
    }, [navState, applyMetadata, applyBaseFilters, applyDateFilters]);

    useEffect(() => {
        fetchSectors();
    }, []);


    return (
        <PageWrapper>
            <PageHeader
                title={[
                    {
                        text: `${pageHeader}`,
                        link: `/databricksADB/${parentLocation}`,
                        state: {
                            startDate: navState?.searchSource === "clusterSpinUpsByCategory" ? navState?.clickedStartDate : startDate,
                            endDate: navState?.searchSource === "clusterSpinUpsByCategory" ? navState?.clickedEndDate : endDate,
                            env,
                            sector,
                            location,
                            selectedDateInString: navState?.searchSource === "clusterSpinUpsByCategory" ? '3M' : selectedDateInString,
                            searchSource: navState?.searchSource,
                            detailedTableTitle: navState?.detailedTableTitle,
                            detailedTableType: navState?.detailedTableType,
                            pageHeader: navState?.pageHeader,
                            pageSubHeader: navState?.pageSubHeader,
                            contentName: navState?.contentName,
                            filters: navState?.filters
                        }
                    },
                    {
                        text: `${pageSubHeader || ''}${contentName && contentName !== 'ALL' ? ' (' + contentName + ')' : ''}`

                    },
                ]}
                hasEllipsis={false}
                isPepwise2Breadcrumb={true}
            />

            <FiltersBar
                location={location}
                // pass the actual useState setters (not single-arg wrappers)
                setLocation={setLocation}
                env={env}
                setEnv={setEnv}
                envOptions={envOptions}
                setEnvOptions={setEnvOptions}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleDateSelect={handleDateSelect}
                selectedDateInString={selectedDateInString}
                setSelectedDateInString={setSelectedDateInString}
                sectorName={sectorName}
                // typed wrapper to handle SetStateAction<string> (function or value)
                setSectorName={(v) => {
                    const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
                    setSectorName(resolved);
                    setSector(resolved);
                }}
                updateHistoryState={updateHistoryState}
                sectorOptionsList={sectorList}
                searchSource={navState?.searchSource}
                useHeaderFilters
            />
            {loader ? (
                <LoaderComponent pt={20} />
            ) : (
                <DetailedViewWidget
                    ref={detailedRef}
                    //detailedTableTitle={detailedTableTitle}
                    detailedTableTitle={getDetailedTableTitleWithTotal()}
                    fetchData={treeListData}
                    detailedTableType={detailedTableType}
                    tableColumns={getColumns(detailedRef, handleRowClick, computeCostLabel, searchSource, detailedTableTitle)}
                    defaultColumnNames={defaultColumnNames(searchSource)}
                    getDetailedTableData={handleGetTreeListTableData}
                    getHierarchyList={() => []}
                    flattenFn={flattenCostData}
                    showExport={false}
                    totalCost={totalSum || 1}
                />
            )}
        </PageWrapper>
    );
};

export default ADBTimeRangeView;